import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../utils/axios.ts';
import LoanApprovalCard from './LoanApprovalCard';

const LoanApprovalQueue = () => {
  const [selectedLoans, setSelectedLoans] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const [bulkAction, setBulkAction] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const queryClient = useQueryClient();

  // Fetch pending approvals
  const { data: pendingLoans, isLoading, error, refetch } = useQuery({
    queryKey: ['pending-approvals', searchTerm, dateFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (dateFilter.from) params.append('created_from', dateFilter.from);
      if (dateFilter.to) params.append('created_to', dateFilter.to);
      
      const response = await api.get(`/memberloan/pending-approvals?${params.toString()}`);
      return response.data;
    },
    staleTime: 30000, // 30 seconds
  });

  // Individual approval mutation
  const approveMutation = useMutation({
    mutationFn: async ({ loanId, notes }) => {
      const response = await api.post(`/memberloan/approve/${loanId}`, {
        reason: notes || 'Approved by admin',
        send_sms: true
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      toast.success('Loan approved successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to approve loan');
    }
  });

  // Individual disapproval mutation
  const disapproveMutation = useMutation({
    mutationFn: async ({ loanId, reason }) => {
      const response = await api.post(`/memberloan/disapprove/${loanId}`, {
        reason: reason,
        send_sms: true
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      toast.success('Loan disapproved successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to disapprove loan');
    }
  });

  // Disbursement mutation
  const disburseMutation = useMutation({
    mutationFn: async ({ loanId, notes }) => {
      const response = await api.post(`/memberloan/${loanId}/disburse`, {
        disbursement_notes: notes,
        send_sms: true
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      toast.success('Loan disbursed successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to disburse loan');
    }
  });

  // Bulk approval mutation
  const bulkApproveMutation = useMutation({
    mutationFn: async ({ loanIds, notes }) => {
      const response = await api.post('/memberloan/bulk-approve', {
        loan_ids: loanIds,
        reason: notes,
        send_sms: true
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      setSelectedLoans(new Set());
      setShowBulkModal(false);
      setBulkNotes('');
      toast.success(`Bulk approval completed: ${data.success_count || 0} approved, ${data.failed_count || 0} failed`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to perform bulk approval');
    }
  });

  // Bulk disapproval mutation
  const bulkDisapproveMutation = useMutation({
    mutationFn: async ({ loanIds, notes }) => {
      const response = await api.post('/memberloan/bulk-disapprove', {
        loan_ids: loanIds,
        reason: notes,
        send_sms: true
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      setSelectedLoans(new Set());
      setShowBulkModal(false);
      setBulkNotes('');
      toast.success(`Bulk disapproval completed: ${data.success_count || 0} disapproved, ${data.failed_count || 0} failed`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to perform bulk disapproval');
    }
  });

  const handleApprove = (loanId, notes) => {
    setIsProcessing(true);
    approveMutation.mutate({ loanId, notes }, {
      onSettled: () => setIsProcessing(false)
    });
  };

  const handleDisapprove = (loanId, reason) => {
    setIsProcessing(true);
    disapproveMutation.mutate({ loanId, reason }, {
      onSettled: () => setIsProcessing(false)
    });
  };

  const handleDisburse = (loanId, notes) => {
    setIsProcessing(true);
    disburseMutation.mutate({ loanId, notes }, {
      onSettled: () => setIsProcessing(false)
    });
  };

  const handleSelectLoan = (loanId, selected) => {
    const newSelected = new Set(selectedLoans);
    if (selected) {
      newSelected.add(loanId);
    } else {
      newSelected.delete(loanId);
    }
    setSelectedLoans(newSelected);
  };

  const handleSelectAll = (selected) => {
    if (selected && pendingLoans?.data) {
      const allIds = pendingLoans.data.map(loan => loan.id);
      setSelectedLoans(new Set(allIds));
    } else {
      setSelectedLoans(new Set());
    }
  };

  const handleBulkAction = () => {
    if (selectedLoans.size === 0) {
      toast.error('Please select at least one loan');
      return;
    }

    if (bulkAction === 'approve') {
      bulkApproveMutation.mutate({
        loanIds: Array.from(selectedLoans),
        notes: bulkNotes
      });
    } else if (bulkAction === 'disapprove') {
      bulkDisapproveMutation.mutate({
        loanIds: Array.from(selectedLoans),
        notes: bulkNotes
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const calculateTotalAmount = () => {
    if (!pendingLoans?.data) return 0;
    return pendingLoans.data.reduce((total, loan) => total + loan.principal, 0);
  };

  const selectedLoansData = pendingLoans?.data?.filter(loan => selectedLoans.has(loan.id)) || [];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      'APPROVED': { color: 'bg-green-100 text-green-800', text: 'Approved' },
      'DISAPPROVED': { color: 'bg-red-100 text-red-800', text: 'Disapproved' },
      'ACTIVE': { color: 'bg-blue-100 text-blue-800', text: 'Active' }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading pending approvals
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {error.message}
            </div>
            <div className="mt-4">
              <button
                onClick={() => refetch()}
                className="bg-red-100 text-red-800 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-200"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Loan Approval Queue</h1>
            <p className="text-gray-600">
              Review and manage pending loan applications
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 kpi-grid">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {pendingLoans?.data?.length || 0}
            </div>
            <div className="text-sm text-blue-600">Pending Loans</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(calculateTotalAmount())}
            </div>
            <div className="text-sm text-green-600">Total Amount</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {selectedLoans.size}
            </div>
            <div className="text-sm text-yellow-600">Selected</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {pendingLoans?.data?.filter(loan => loan.requires_documents && !loan.has_all_required_documents).length || 0}
            </div>
            <div className="text-sm text-purple-600">Missing Documents</div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by member name, loan number..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Date
            </label>
            <input
              type="date"
              value={dateFilter.from}
              onChange={(e) => setDateFilter({ ...dateFilter, from: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To Date
            </label>
            <input
              type="date"
              value={dateFilter.to}
              onChange={(e) => setDateFilter({ ...dateFilter, to: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedLoans.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-blue-800">
                  {selectedLoans.size} loan(s) selected
                </span>
                <span className="text-sm text-blue-600">
                  Total: {formatCurrency(selectedLoansData.reduce((total, loan) => total + loan.principal, 0))}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Action</option>
                  <option value="approve">Bulk Approve</option>
                  <option value="disapprove">Bulk Disapprove</option>
                </select>
                <button
                  onClick={() => setShowBulkModal(true)}
                  disabled={!bulkAction}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loan Cards */}
      <div className="space-y-4">
        {/* Select All */}
        {pendingLoans?.data && pendingLoans.data.length > 0 && (
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={selectedLoans.size === pendingLoans.data.length}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700">
                Select All ({pendingLoans.data.length} loans)
              </span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && (!pendingLoans?.data || pendingLoans.data.length === 0) && (
          <div className="bg-white shadow rounded-lg p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No pending approvals</h3>
            <p className="text-gray-500">All loan applications have been processed.</p>
          </div>
        )}

        {/* Loan Cards */}
        {!isLoading && pendingLoans?.data && pendingLoans.data.map((loan) => (
          <LoanApprovalCard
            key={loan.id}
            loan={loan}
            onApprove={handleApprove}
            onDisapprove={handleDisapprove}
            onDisburse={handleDisburse}
            isProcessing={isProcessing}
            selected={selectedLoans.has(loan.id)}
            onSelect={handleSelectLoan}
          />
        ))}
      </div>

      {/* Bulk Action Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {bulkAction === 'approve' ? 'Bulk Approve' : 'Bulk Disapprove'} Loans
            </h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                {selectedLoans.size} loan(s) will be {bulkAction === 'approve' ? 'approved' : 'disapproved'}
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {bulkAction === 'approve' ? 'Approval' : 'Disapproval'} Notes
                {bulkAction === 'disapprove' && <span className="text-red-500"> *</span>}
              </label>
              <textarea
                value={bulkNotes}
                onChange={(e) => setBulkNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder={bulkAction === 'approve' ? 'Add approval notes...' : 'Provide reason for disapproval...'}
                required={bulkAction === 'disapprove'}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkNotes('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAction}
                disabled={bulkAction === 'disapprove' && !bulkNotes.trim()}
                className={`px-4 py-2 text-white rounded-md disabled:opacity-50 ${
                  bulkAction === 'approve' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {bulkAction === 'approve' ? 'Approve All' : 'Disapprove All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanApprovalQueue; 